import { changed, noOp, type BrushSettings, type Command, type CommandExecution, type SampledBrushTip } from './commands';
import { createId, type EditorDocument } from './model';
import type { SelectionCombineMode } from './selection';
import type { ExposureRange } from './exposure-tools';

export const TOOL_PRESETS_EXTENSION = 'maipo:tool-presets';
export const TOOL_PRESET_IDS = ['move', 'marquee', 'quick-selection', 'note', 'shape', 'pen', 'shaper', 'knife', 'brush', 'history-brush', 'clone-stamp', 'healing-brush', 'dodge', 'burn', 'eraser', 'gradient', 'hand', 'zoom'] as const;
export type ToolPresetToolId = typeof TOOL_PRESET_IDS[number];
export type ShapeToolKind = 'rectangle' | 'ellipse' | 'rectangular-grid' | 'polar-grid';
export interface GridToolSettings {
  horizontalDividers: number;
  verticalDividers: number;
  concentricDividers: number;
  radialDividers: number;
}

export interface ToolPresetSettings {
  brush?: BrushSettings;
  selectionMode?: SelectionCombineMode;
  shapeKind?: ShapeToolKind;
  gridSettings?: GridToolSettings;
  quickSelectionTolerance?: number;
  perspectiveClone?: boolean;
  exposure?: { amount: number; range: ExposureRange; protectTones: boolean };
}

export interface ToolPreset {
  id: string;
  name: string;
  tool: ToolPresetToolId;
  settings: ToolPresetSettings;
}

function validPreset(value: unknown): value is ToolPreset {
  if (!value || typeof value !== 'object') return false;
  const preset = value as Partial<ToolPreset>;
  return typeof preset.id === 'string' && typeof preset.name === 'string'
    && TOOL_PRESET_IDS.some((tool) => tool === preset.tool)
    && Boolean(preset.settings && typeof preset.settings === 'object');
}

function finite(value: unknown, fallback: number, minimum: number, maximum: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(minimum, Math.min(maximum, value)) : fallback;
}

/**
 * A sampled brush tip is user-authored data, so it is validated in place rather
 * than dropped: the preset list is rewritten wholesale from `toolPresets` on
 * every save, rename and delete, so anything omitted here is destroyed on disk.
 */
function sampledTip(value: unknown): SampledBrushTip | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const source = value as Partial<SampledBrushTip>;
  const width = typeof source.width === 'number' ? Math.round(source.width) : 0;
  const height = typeof source.height === 'number' ? Math.round(source.height) : 0;
  if (width < 1 || height < 1 || width > 512 || height > 512 || !Array.isArray(source.alpha) || source.alpha.length !== width * height) return undefined;
  return { width, height, alpha: source.alpha.map((entry) => Math.round(finite(entry, 0, 0, 255))) };
}

const SELECTION_MODES: readonly SelectionCombineMode[] = ['replace', 'add', 'subtract', 'intersect'];
const SHAPE_KINDS: readonly ShapeToolKind[] = ['rectangle', 'ellipse', 'rectangular-grid', 'polar-grid'];
const EXPOSURE_RANGES: readonly ExposureRange[] = ['shadows', 'midtones', 'highlights'];

/**
 * Presets are applied straight onto live tool state, so every field has to be
 * clamped on the way out of the document. Every other resource in this editor
 * validates on load; a preset carrying `size: NaN` from a corrupted or
 * hand-edited `.maipo` would otherwise break painting outright.
 */
function normalizeSettings(value: unknown): ToolPresetSettings {
  const source = value && typeof value === 'object' ? value as Partial<ToolPresetSettings> : {};
  const settings: ToolPresetSettings = {};
  if (source.brush && typeof source.brush === 'object') {
    const brush = source.brush;
    const tip = sampledTip(brush.tip);
    settings.brush = {
      size: finite(brush.size, 42, 1, 1_000),
      hardness: finite(brush.hardness, 0.7, 0, 1),
      opacity: finite(brush.opacity, 1, 0.01, 1),
      flow: finite(brush.flow, 0.8, 0.01, 1),
      spacing: finite(brush.spacing, 0.12, 0.01, 5),
      color: Array.isArray(brush.color) && brush.color.length === 4
        ? brush.color.map((channel) => Math.round(finite(channel, 0, 0, 255))) as unknown as BrushSettings['color']
        : [0, 0, 0, 255],
      mode: brush.mode === 'erase' ? 'erase' : 'paint',
      angle: finite(brush.angle, 0, -180, 180),
      roundness: finite(brush.roundness, 1, 0.05, 1),
      scatter: finite(brush.scatter, 0, 0, 2),
      count: Math.round(finite(brush.count, 1, 1, 8)),
      texture: finite(brush.texture, 0, 0, 1),
      pressureSize: Boolean(brush.pressureSize),
      pressureOpacity: brush.pressureOpacity !== false,
      sizeJitter: finite(brush.sizeJitter, 0, 0, 1),
      opacityJitter: finite(brush.opacityJitter, 0, 0, 1),
      ...(tip ? { tip } : {}),
    };
  }
  const selectionMode = SELECTION_MODES.find((mode) => mode === source.selectionMode);
  if (selectionMode) settings.selectionMode = selectionMode;
  const shapeKind = SHAPE_KINDS.find((kind) => kind === source.shapeKind);
  if (shapeKind) settings.shapeKind = shapeKind;
  if (source.gridSettings && typeof source.gridSettings === 'object') {
    settings.gridSettings = {
      horizontalDividers: Math.round(finite(source.gridSettings.horizontalDividers, 0, 0, 64)),
      verticalDividers: Math.round(finite(source.gridSettings.verticalDividers, 0, 0, 64)),
      concentricDividers: Math.round(finite(source.gridSettings.concentricDividers, 0, 0, 64)),
      radialDividers: Math.round(finite(source.gridSettings.radialDividers, 0, 0, 64)),
    };
  }
  if (source.quickSelectionTolerance !== undefined) settings.quickSelectionTolerance = finite(source.quickSelectionTolerance, 32, 0, 255);
  if (source.perspectiveClone !== undefined) settings.perspectiveClone = Boolean(source.perspectiveClone);
  if (source.exposure && typeof source.exposure === 'object') {
    settings.exposure = {
      amount: finite(source.exposure.amount, 0.25, 0.01, 1),
      range: EXPOSURE_RANGES.find((range) => range === source.exposure?.range) ?? 'midtones',
      protectTones: source.exposure.protectTones !== false,
    };
  }
  return settings;
}

export function toolPresets(document: EditorDocument): ToolPreset[] {
  const value = document.extensions[TOOL_PRESETS_EXTENSION];
  if (!Array.isArray(value)) return [];
  return value.filter(validPreset).slice(0, 200).map((preset) => ({
    id: preset.id,
    name: preset.name.slice(0, 100),
    tool: preset.tool,
    settings: normalizeSettings(preset.settings),
  }));
}

export function createToolPreset(name: string, tool: ToolPresetToolId, settings: ToolPresetSettings): ToolPreset {
  return { id: createId('tool-preset'), name: name.trim().slice(0, 100) || `${tool} preset`, tool, settings: structuredClone(settings) };
}

export class SetToolPresetsCommand implements Command {
  readonly label: string;
  private before?: unknown;
  private hadBefore = false;

  constructor(private readonly presets: readonly ToolPreset[], label = 'Update tool presets') {
    this.label = label;
  }

  retainedBytes(): number {
    return 128 + JSON.stringify(this.presets).length * 2 + JSON.stringify(this.before ?? null).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    if (JSON.stringify(toolPresets(document)) === JSON.stringify(this.presets)) return noOp();
    if (this.before === undefined) {
      this.hadBefore = Object.hasOwn(document.extensions, TOOL_PRESETS_EXTENSION);
      this.before = structuredClone(document.extensions[TOOL_PRESETS_EXTENSION] ?? null);
    }
    if (this.presets.length) document.extensions[TOOL_PRESETS_EXTENSION] = structuredClone(this.presets.slice(0, 200));
    else delete document.extensions[TOOL_PRESETS_EXTENSION];
    return changed();
  }

  undo(document: EditorDocument): void {
    if (this.hadBefore) document.extensions[TOOL_PRESETS_EXTENSION] = structuredClone(this.before);
    else delete document.extensions[TOOL_PRESETS_EXTENSION];
  }
}
